import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ContactValidator} from '../../helpers/contact-validator';
import {CustomValidators} from '@narik/custom-validators';
import {ConfirmService, HttpLoaderService, SnackbarService, T4Validators} from '@tymes4-shared';
import {AccountManagerModel, AccountManagerService, ConfigService, ContactService, CustomerModel, CustomerParentRelationshipView, CustomerService, SystemConfigurationSettingsService} from '../../api';
import {ApplicationModuleHelperService, ApplicationModules} from '../../services/application-module.service';
import {setRequiredFields} from './edit-customer-form.validator';
import {createPrimaryContactForm} from '../edit-customer-primary-account/edit-customer-primary-account.form';
import { snackbarGoToNewCustomer } from '../../components/snackbar-go-to-new-customer/snackbar-go-to-new-customer.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-edit-customer-form',
    templateUrl: './edit-customer-form.component.html',
    styleUrls: ['./edit-customer-form.component.scss'],
    standalone: false
})

export class EditCustomerFormComponent implements OnInit {
  customerId: number = 0;
  mainForm: FormGroup;
  PrimaryContact: FormGroup;
  Address: FormGroup;
  public customer: any = {};
  accountManagers: Array<AccountManagerModel> = null;
  private businessTypeId = 1;
  private readonly customerReworkDialogWidth = '720px';
  isNewCustomer: boolean = false;
  isCustomerReworkActive: boolean = false;
  public saving = false;
  readonly requiredValidator = Validators.required;
  public canDegradeAccountToUnderlying: boolean = false;
  validationMessages = {};
  public allowProfileEdit: boolean = true;

  constructor(@Inject(MAT_DIALOG_DATA) public passedData: any,
              public dialogRef: MatDialogRef<EditCustomerFormComponent>,
              private customerService: CustomerService,
              private contactValidator: ContactValidator,
              private loader: HttpLoaderService,
              private snackBarGoToCustomer: MatSnackBar,
              private accountManagerService: AccountManagerService,
              private confirmService: ConfirmService,
              private snackBar: SnackbarService,
              private translate: TranslateService,
              private config: SystemConfigurationSettingsService,
              private contactService: ContactService,
              private applicationModuleHelperService: ApplicationModuleHelperService,
              private configService: ConfigService) {
  }

  ngOnInit() {
    this.customerId = this.passedData.payload;
    this.isNewCustomer = this.passedData.isNew;

    // NOTE: when the customer type is changed, it is overwitten which controls are required and which are not.
    // For fans, the list of required fields is in a configuration setting.
    // For companies, the list of required fields is in the method subscribeToCustomerTypeChanges.

    this.mainForm = new FormGroup({
      Id: new FormControl(null),
      PaysByInvoice: new FormControl(), //here to preserve value
      Name: new FormControl('', [Validators.maxLength(512)]),
      COC: new FormControl(''),
      CustomerType: new FormControl('', [Validators.required]),
      AccountManagerId: new FormControl(null),
      TelephoneNr: new FormControl('', [T4Validators.validatePhonenumber]),
      Email: new FormControl(null, [CustomValidators.email, Validators.maxLength(512)]),
      ExternalCode: new FormControl(''),
      VATNr: new FormControl(''),
      ExternalId: new FormControl(''),
      InvoiceAddressId: new FormControl(null)
    });
    this.PrimaryContact = createPrimaryContactForm(this.contactValidator);
    this.Address = new FormGroup({
      Street: new FormControl('', [Validators.required]),
      StreetNr: new FormControl('', [CustomValidators.number, Validators.required]),
      StreetNrAddition: new FormControl(''),
      PostalCode: new FormControl('', [Validators.required]),
      CountryId: new FormControl('', [Validators.required]),
      City: new FormControl('', [Validators.required])
    });
    this.accountManagerService.listActiveAccountManagers().subscribe((data: Array<AccountManagerModel>) => {
      this.accountManagers = data;
    });

    this.subscribeToCustomerTypeChanges();
    this.applicationModuleHelperService.getActiveModules().subscribe(activeModules => {
      this.isCustomerReworkActive = activeModules?.includes(ApplicationModules.CustomerRework) ?? false;
      if (this.isCustomerReworkActive) {
        this.dialogRef.updateSize(this.customerReworkDialogWidth);
      }
      this.updateCompanyEmailState();
      if (!this.isNewCustomer) {
        this.loadCustomerForEdit();
      } else {
        this.contactValidator.customerId = null;
        this.contactValidator.currentEmail = null;
        this.customer = {PrimaryContact: {}, Address: {}};
        this.mainForm.patchValue(this.customer);
        this.PrimaryContact.patchValue(this.customer.PrimaryContact ?? {});
        this.Address.patchValue(this.customer.Address);
      }
    });
  }

  get hidePrimaryAccount(): boolean {
    return this.isCustomerReworkActive && this.mainForm.controls.CustomerType.value === this.businessTypeId;
  }

  get isBusinessCustomer(): boolean {
    return this.mainForm.controls.CustomerType.value == this.businessTypeId;
  }

  get showCompanyEmail(): boolean {
    return this.isCustomerReworkActive && this.isBusinessCustomer;
  }

  private updatePrimaryContactState() {
    if (this.hidePrimaryAccount) {
      this.allowProfileEdit = true;
      this.PrimaryContact.disable();
      return;
    }

    // disable all fields when we use SSO
    this.PrimaryContact.disable();
    this.determineAllowProfileEditing(this.customer).subscribe(result => {
      if (this.hidePrimaryAccount) return;

      this.allowProfileEdit = result;
      if (!this.allowProfileEdit) {
        this.disableMainformFields();
        this.Address.disable();
      } else {
        this.mainForm.enable({emitEvent: false});
        this.updateCompanyEmailState();
        this.PrimaryContact.enable();
        this.Address.enable();
        if (this.customer.PrimaryContact?.SSOId) {
          this.PrimaryContact.controls.Email.disable();
          this.PrimaryContact.controls.SSOId.disable();
          this.mainForm.controls.CustomerType.disable({emitEvent: false});
        }
      }
    });
  }

  private updateRequiredFieldsFromConfig() {
    this.config.getCustomerFormRequiredFields().subscribe(fields => {
      const requiredFields = fields.split(';');
      setRequiredFields(this.mainForm, requiredFields);
      setRequiredFields(this.PrimaryContact, requiredFields);
      setRequiredFields(this.Address, requiredFields);
    });
  }

  private subscribeToCustomerTypeChanges() {
    this.mainForm.get('CustomerType').valueChanges.subscribe((type: number) => {
      if (type === this.businessTypeId) {
        setRequiredFields(this.mainForm, ["Name"]);
        setRequiredFields(this.PrimaryContact, ["FirstName", "LastName"]);
        setRequiredFields(this.Address, ["Street", "StreetNr", "PostalCode", "CountryId", "City"]);
      } else {
        this.updateRequiredFieldsFromConfig();
      }
      this.updatePrimaryContactState();
      this.updateCompanyEmailState();
    });
  }

  updateCompanyEmailState() {
    const email = this.mainForm.controls.Email;
    if (this.showCompanyEmail) {
      email.enable({emitEvent: false});
    } else {
      email.disable({emitEvent: false});
    }
  }

  loadCustomerForEdit() {
    this.customer = {};
    this.customerService.getCustomerWithContacts(this.customerId).subscribe((data: CustomerModel) => {
      //set the customer on screen and load the address
      this.customer = data;

      this.contactValidator.currentEmail = this.customer.Email;
      this.contactValidator.customerId = this.customer.Id;
      this.mainForm.patchValue(this.customer);
      this.PrimaryContact.patchValue(this.customer.PrimaryContact ?? {});
      this.Address.patchValue(this.customer.Address);
    });

    this.customerService.getParent(this.customerId, false).subscribe((data: CustomerParentRelationshipView) => {
      if (data && !data["IsManagedByParent"]) {
        this.canDegradeAccountToUnderlying = true;
      }
    });
  }

  disableMainformFields() {
    this.mainForm.controls.CustomerType.disable({emitEvent: false});
    this.mainForm.controls.TelephoneNr.disable();
    this.mainForm.controls.Name.disable();
  }

  customerIsValidAndDirty() {
    const anyFieldDirty = this.mainForm.dirty || this.PrimaryContact.dirty || this.Address.dirty;
    if (!anyFieldDirty) {
      return false;
    }

    let isValid: boolean =
      (this.PrimaryContact.valid || this.PrimaryContact.disabled) &&
      (this.Address.valid || this.Address.disabled);

    const activeType = this.mainForm.get('CustomerType').value;
    if (activeType == this.businessTypeId) {
      isValid &&= this.mainForm.valid;
    }

    return isValid;
  }

  submit() {
    // mainForm may contain disabled controls, in which case mainForm.value does not include those properties.
    // However, the backend currently lacks validation and interprets missing properties as them being set to null, but we just want to keep the values unchanged for disabled controls.
    // Therefore, mainForm.getRawValue() is used here so that also the unchanged disabled fields are sent to the backend.
    const customer = this.mainForm.getRawValue();

    customer.Email = this.companyEmailForSubmit(customer.Email);
    customer.PrimaryContact = this.hidePrimaryAccount ? undefined : this.PrimaryContact.getRawValue();
    customer.Address = this.Address.getRawValue();

    // Prevents email validation
    if (customer.PrimaryContact?.Email == '') {
      customer.PrimaryContact.Email = null;
    }

    if (!this.isNewCustomer) {
      this.loader.open();
      this.saving = true;
      this.customerService.updateCustomer(customer)
        .pipe(finalize(() => {
          this.loader.close();
          this.saving = false;
        }))
        .subscribe((data: boolean) => {
          this.dialogRef.close(true);
          this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.CUSTOMER-SAVED'), 'GENERIC.SNACKBAR.CLOSE');
        });
    } else {
      this.customerService.createCustomer(customer).subscribe((data: number) => {
        this.loader.close();
        this.dialogRef.close(data);
        this.saving = false;
        this.snackBarGoToCustomer.openFromComponent(snackbarGoToNewCustomer, {duration: 12500, data: data});
      });
    }
  }

  degradeAccountToUnderlying() {
    const title = this.translate.instant('DIALOG.DEGRADE_ACCOUNT.CONFIRM.TITLE');
    const message = this.translate.instant('DIALOG.DEGRADE_ACCOUNT.CONFIRM.MSG');
    this.confirmService.confirm({ title: title, message: message, okonly: false}).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.loader.open();
      this.saving = true;
      this.contactService.degradeAccountToUnderlying(this.customerId).subscribe(contact => {
        this.loader.close();
        this.dialogRef.close(true);
        this.saving = false;
        this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.CUSTOMER-SAVED'), 'GENERIC.SNACKBAR.CLOSE');
      });
    });
  }

  private companyEmailForSubmit(email: string): string {
    if (!this.showCompanyEmail) {
      return undefined;
    }
    return email || null;
  }

  determineAllowProfileEditing(customer: CustomerModel): Observable<boolean> {
    if (customer.PrimaryContact?.SSOId) {
      return this.configService.allowSSOWriteBack();
    }
    return of(true);
  }
}
